import { Box, Button, IconButton, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { Routes } from '@src/navigation/Routes';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ScanProcedureDTO from '@src/adapters/dto/scan/ScanProcedureDTO';

interface Props {
  procedures: ScanProcedureDTO[];
  numberOfPages: number;
  currentPage: number;
  selectedIndex: number;
  setPageNumber(page: number): void;
  previous(): void;
  next(): void;
  updateRotation(rotation: number): void;
  zoom(increase: boolean): void;
}

function Footer({
  procedures,
  numberOfPages,
  currentPage,
  selectedIndex,
  setPageNumber,
  previous,
  next,
  updateRotation,
  zoom,
}: Props) {
  const [page, setPage] = useState(1);
  const [, setRotation] = useState(0);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const recordCount = useMemo(() => {
    return procedures.flatMap((procedure) => procedure.scans).length;
  }, [procedures]);

  const completedItems = useMemo(() => {
    return procedures
      .flatMap((procedure) => procedure.scans)
      .filter((a) => a.isDoNotMigrate || a.isMigrate).length;
  }, [procedures]);

  const handleBack = () => {
    if (currentPage > 1) {
      setPageNumber(currentPage - 1);
    }
  };

  const handleForward = () => {
    if (currentPage < numberOfPages) {
      setPageNumber(currentPage + 1);
    }
  };

  const rotate = (left: boolean) => {
    setRotation((prevRotation: number) => {
      let rota = (prevRotation - (left ? 90 : -90)) % 360;

      if (rota < 0) {
        //ensure rotation is always positive
        rota += 360;
      }

      updateRotation(rota);
      return rota;
    });
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        backgroundColor: '#fff',
        padding: theme.spacing(4),
        height: theme.spacing(4.5),
      })}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          width: '33%',
        }}>
        <IconButton
          aria-label="back"
          onClick={() => rotate(true)}
          size="medium"
          color="primary">
          <RotateLeftIcon />
        </IconButton>
        <IconButton
          aria-label="back"
          onClick={() => rotate(false)}
          size="medium"
          color="primary">
          <RotateRightIcon />
        </IconButton>
        <IconButton
          aria-label="back"
          onClick={() => zoom(false)}
          size="medium"
          color="primary">
          <ZoomOutIcon />
        </IconButton>
        <IconButton
          aria-label="back"
          onClick={() => zoom(true)}
          size="medium"
          color="primary">
          <ZoomInIcon />
        </IconButton>
        {numberOfPages > 1 && (
          <>
            <Typography variant="body1">
              Page {page} of {numberOfPages}
            </Typography>
            <Box>
              <IconButton
                aria-label="back"
                onClick={handleBack}
                size="medium"
                color="primary">
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                aria-label="back"
                onClick={handleForward}
                size="medium"
                color="primary">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '34%',
        }}>
        <Typography variant="body1">
          {selectedIndex !== undefined &&
            `${
              selectedIndex + 1
            } of ${recordCount} - (${completedItems} completed)`}
        </Typography>
      </Box>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'right',
          alignItems: 'center',
          width: '33%',
          gap: theme.spacing(2),
        })}>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={previous}
          disabled={selectedIndex === 0}>
          <ArrowBackIcon />
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={next}
          disabled={selectedIndex === recordCount - 1}>
          <ArrowForwardIcon />
        </Button>
        <Button variant="outlined" size="medium" color="primary">
          <Link to={Routes.ScanReview} style={{ textDecoration: 'none' }}>
            Close
          </Link>
        </Button>
      </Box>
    </Box>
  );
}

export default Footer;
